/**
 * Banner de la clínica (inicio de sesión y página principal): colores de fondo editables, logotipo a la izquierda,
 * nombre grande y «With Donna» abajo a la derecha. Aquí vive lo que no depende de la pantalla: valores por defecto,
 * validación de colores y color del texto, para que el servidor y la web digan lo mismo.
 */
import { ACENTOS } from "./configuracion.ts";

export type TextoBanner = "auto" | "claro" | "oscuro";
export interface BannerClinica { activo: boolean; color1: string; color2: string; texto: TextoBanner }

const HEX = /^#[0-9a-fA-F]{6}$/;
export const TEXTOS_BANNER: readonly TextoBanner[] = ["auto", "claro", "oscuro"];
export const TEXTO_CLARO = "#FFFFFF";
export const TEXTO_OSCURO = "#10302A";

/** Sin configurar, el banner usa el color de acento de la clínica. */
export function bannerPorDefecto(acento: string): BannerClinica {
  const c = (ACENTOS[acento] ?? ACENTOS.verde).claro;
  return { activo: true, color1: c, color2: c, texto: "auto" };
}

/** Lo que se puede guardar o mostrar: lo inválido cae al valor por defecto (nunca se devuelve texto libre). */
export function normalizarBanner(x: unknown, acento: string): BannerClinica {
  const d = bannerPorDefecto(acento);
  if (!x || typeof x !== "object") return d;
  const o = x as Record<string, unknown>;
  const color = (v: unknown, def: string) => (typeof v === "string" && HEX.test(v) ? v.toUpperCase() : def);
  const color1 = color(o.color1, d.color1);
  return {
    activo: o.activo !== false,
    color1,
    color2: color(o.color2, color1),
    texto: TEXTOS_BANNER.includes(o.texto as TextoBanner) ? (o.texto as TextoBanner) : "auto",
  };
}

/** Mensajes para el administrador si los datos del banner no sirven (vacío = todo bien). */
export function problemasBanner(x: unknown): string[] {
  if (!x || typeof x !== "object" || Array.isArray(x)) return ["Los datos del banner no son válidos."];
  const o = x as Record<string, unknown>;
  const p: string[] = [];
  for (const [k, n] of [["color1", "El color de fondo"], ["color2", "El segundo color"]] as const)
    if (o[k] !== undefined && !(typeof o[k] === "string" && HEX.test(o[k] as string))) p.push(`${n} del banner no es válido (usa el selector de color).`);
  if (o.texto !== undefined && !TEXTOS_BANNER.includes(o.texto as TextoBanner)) p.push("El color del texto del banner no es válido.");
  if (o.activo !== undefined && typeof o.activo !== "boolean") p.push("«Mostrar el banner» debe ser sí o no.");
  return p;
}

/** Luminosidad percibida de un color #RRGGBB, de 0 (negro) a 1 (blanco). */
export function luminosidad(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

/** Color del texto del banner: blanco sobre fondos oscuros, oscuro sobre fondos claros (o el que elija el administrador). */
export function colorTextoBanner(b: BannerClinica): string {
  if (b.texto === "claro") return TEXTO_CLARO;
  if (b.texto === "oscuro") return TEXTO_OSCURO;
  return (luminosidad(b.color1) + luminosidad(b.color2)) / 2 < 0.6 ? TEXTO_CLARO : TEXTO_OSCURO;
}

/** Fondo CSS del banner: un color, o un degradado suave si hay dos distintos. Solo sale de colores #RRGGBB ya validados. */
export function fondoBanner(b: BannerClinica): string {
  return b.color1.toUpperCase() === b.color2.toUpperCase() ? b.color1 : `linear-gradient(90deg, ${b.color1}, ${b.color2})`;
}
